import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';

interface AdUnitProps {
  /** AdSense ad-unit id (data-ad-slot). */
  slot: string;
  /** Tailwind min-height. Reserve the box up front: Google's responsive <ins> resolves
   *  around 5s and keeps its height even when it does not fill, so an unreserved slot
   *  shifts everything below it (that cost 0.4-0.58 CLS before MobileArticleAd was fixed). */
  reserve?: string;
  className?: string;
  /** Render and request the ad only once the box is within ~1,200px of the viewport
   *  (default). Every AdUnit sits at the end of a page, which most visitors never reach;
   *  requesting at mount spent requests that could not be seen (2026-10-01). The <ins>
   *  is not in the DOM until then, so an unrequested slot can never absorb another
   *  unit's push. */
  lazy?: boolean;
}

/**
 * One AdSense slot, with the two rules the article slot learned the hard way: reserve the
 * height before the ad arrives, and never render for signed-in readers. `isAuthenticated`
 * comes from a token read synchronously from localStorage, so it is right on first render;
 * `loading` only defers the adsbygoogle push.
 */
const AdUnit: React.FC<AdUnitProps> = ({ slot, reserve = 'min-h-[280px]', className = '', lazy = true }) => {
  const { isAuthenticated, loading } = useAuth();
  const pushed = useRef(false);
  const box = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(!lazy);

  useEffect(() => {
    if (near || isAuthenticated) return;
    const el = box.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: '1200px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near, isAuthenticated]);

  useEffect(() => {
    if (!near || loading || isAuthenticated || pushed.current || typeof window === 'undefined') return;
    try {
      const w = window as unknown as { adsbygoogle?: unknown[] };
      (w.adsbygoogle = w.adsbygoogle || []).push({});
      pushed.current = true;
    } catch {
      // adsbygoogle.js has not loaded yet - the next render pushes again
    }
  }, [near, isAuthenticated, loading]);

  if (isAuthenticated) return null;

  return (
    <div ref={box} className={`${reserve} ${className}`.trim()}>
      {near && (
      <ins
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client="ca-pub-9243961427413079"
        data-ad-slot={slot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
      )}
    </div>
  );
};

export default AdUnit;
